import type { FC } from "react";
import { useState } from "react";
import { Button, IconButton, LinearProgress, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";
import type { TaskEntityType, UpdateTaskRequest } from "./tasksApi";
import { useEntityTasks } from "./useEntityTasks";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PersonIcon from "@mui/icons-material/Person";
import GroupsIcon from "@mui/icons-material/Groups";
import { useAuthContext } from "../auth/AuthContext";
import { useAgents } from "../admin/agents/useAgents";
import { getAgentIdIfPresent } from "../auth/auth";
import TaskActions from "./TaskActions";
import EditTaskDialog from "./EditTaskDialog";

interface AddTaskProps {
  readonly onAdd: (request: UpdateTaskRequest) => Promise<void>;
}

const AddTask: FC<AddTaskProps> = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Dodaj
      </Button>
      <EditTaskDialog
        open={open}
        onClose={() => setOpen(false)}
        onSave={onAdd}
        title="Dodaj zadanie"
        submitButtonText="Dodaj"
      />
    </>
  );
};

interface TaskListItemProps {
  readonly task: {
    readonly description: string;
    readonly assignedUser: { readonly id: string; readonly name: string };
    readonly completed?: {
      readonly at: string;
      readonly by: string;
      readonly note?: string;
    };
    readonly due: { readonly date: string; readonly time?: string };
    readonly createdBy: string;
  };
  readonly canDelete: boolean;
  readonly onDelete: () => void;
  readonly onComplete: (note?: string) => Promise<void>;
  readonly onUpdate: (request: UpdateTaskRequest) => Promise<void>;
}

const TaskListItem: FC<TaskListItemProps> = ({ task, canDelete, onDelete, onComplete, onUpdate }) => {
  const { description, due, assignedUser, createdBy } = task;
  const duePretty = due.time
    ? dayjs(`${due.date} ${due.time}`, `${ISO_DATE_FORMAT} HH:mm`).format("DD.MM.YYYY HH:mm")
    : dayjs(due.date, ISO_DATE_FORMAT).format("DD.MM.YYYY");
  const isCompleted = !!task.completed;

  const [editOpen, setEditOpen] = useState(false);

  return (
    <>
      <ListItem
        divider
        secondaryAction={
          <TaskActions
            isCompleted={isCompleted}
            iconsDirection="row"
            canDelete={canDelete}
            onComplete={onComplete}
            onDelete={onDelete}
            onEdit={() => setEditOpen(true)}
          />
        }
      >
        <ListItemText
          primary={description}
          secondary={`${duePretty} • ${assignedUser.name} • Utworzono przez: ${createdBy}`}
        />
      </ListItem>
      <EditTaskDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={onUpdate}
        initialValues={{ description, due, assignedUserId: assignedUser.id }}
        title="Edytuj zadanie"
        submitButtonText="Zapisz"
      />
    </>
  );
};

interface Props {
  readonly entityType: TaskEntityType;
  readonly entityId: string;
  readonly customerId: string;
}

const Tasks: FC<Props> = ({ entityType, entityId, customerId }) => {
  const { context, user } = useAuthContext();
  const {
    tasks,
    isLoading: areTasksLoading,
    createTask,
    deleteTask,
    completeTask,
    updateTask,
  } = useEntityTasks(entityType, entityId, customerId);
  const { agents, isLoading: areAgentsLoading } = useAgents();
  const [showAll, setShowAll] = useState(false);
  const [showMineOnly, setShowMineOnly] = useState(true);

  const isLoading = areTasksLoading || areAgentsLoading;
  const currentUserId = getAgentIdIfPresent(context);
  const visibleTasks = tasks
    .filter((t) => (showAll ? true : !t.completed))
    .filter((t) => (showMineOnly ? t.assignedUser === currentUserId : true))
    .map((t) => ({
      ...t,
      assignedUser: { id: t.assignedUser, name: agents.find((a) => a.id === t.assignedUser)?.name ?? "n.d." },
      createdBy: agents.find((a) => a.loginEmail === t.createdBy)?.name ?? t.createdBy,
      createdByEmail: t.createdBy,
    }));
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <AddTask
          onAdd={async ({ description, due, assignedUser }) => {
            await createTask({ description, due, assignedUser });
          }}
        />
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={() => setShowMineOnly(!showMineOnly)}
            title={showMineOnly ? "Pokaż wszystkie" : "Pokaż tylko moje"}
          >
            {showMineOnly ? <PersonIcon /> : <GroupsIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setShowAll(!showAll)}
            title={showAll ? "Ukryj wykonane" : "Pokaż wykonane"}
          >
            {showAll ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Stack>
      </Stack>
      {isLoading && <LinearProgress />}
      <List>
        {visibleTasks.map((task) => (
          <TaskListItem
            key={task.id}
            task={task}
            canDelete={task.createdByEmail === user?.email}
            onDelete={() => deleteTask(task.id)}
            onComplete={async (note) => {
              await completeTask(task.id, note);
            }}
            onUpdate={async (r) => {
              await updateTask(task.id, r);
            }}
          />
        ))}
        {visibleTasks.length === 0 && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Brak zadań
          </Typography>
        )}
      </List>
    </Stack>
  );
};

export default Tasks;
